import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { IAuditTrailAPI } from "./auditTrailApi";
import type { AuditTrail, AuditTrailFilters, AuditTrailSort } from "@/types/AuditTrail";
import type { PaginatedResponse } from "@/types/Pagination";

class RemoteAuditTrailAPI implements IAuditTrailAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchAuditTrail(
    filters?: AuditTrailFilters,
    sort?: AuditTrailSort,
    limit?: number,
    skip?: number,
  ): Promise<PaginatedResponse<AuditTrail>> {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (filters) {
        if (filters.entityType) params.append("entityType", filters.entityType);
        if (filters.entityId) params.append("entityId", filters.entityId);
        if (filters.memberId) params.append("memberId", filters.memberId);
        if (filters.action) params.append("action", filters.action);
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);
      }

      if (sort) {
        params.append("sortField", sort.field);
        params.append("sortOrder", sort.order);
      }

      if (limit !== undefined) params.append("limit", limit.toString());
      if (skip !== undefined) params.append("skip", skip.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/audit-trail${queryString ? `?${queryString}` : ""}`;

      const response = await fetchWithAuth(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: true,
            data: [],
            pagination: {
              total: 0,
              limit: limit ?? 200,
              skip: skip ?? 0,
              hasMore: false,
              returned: 0,
            },
          };
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: PaginatedResponse<AuditTrail> = await response.json();
      return result;
    } catch (error) {
      log("remoteAuditTrailApi", "Failed to fetch audit trail", error);
      throw error;
    }
  }
}

export default RemoteAuditTrailAPI;
